// src/components/SideBar.tsx
//
// Resolver: renders the redesigned grouped nav (docs/design/phase1-independent-gui-base.md
// §1-2, migration slice 1 in docs/design/phase2-refinement.md §5) when the new GUI is
// active for this user, otherwise the pre-redesign flat nav in SideBar.legacy.tsx.
// Role visibility below is copied verbatim from the legacy file — this slice only
// regroups and relabels, it does not change who can see what.
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiUsers, FiUserCheck, FiUserPlus, FiKey, FiHome, FiServer, FiCalendar, FiSearch, FiBarChart2, FiLock, FiSettings, FiTrash2, FiBox, FiHardDrive, FiLayers, FiMonitor, FiShield, FiHelpCircle } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { useSidebar } from '../../contexts/SidebarContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { useNewGui } from '../../hooks/useNewGui';
import { useRolePreview } from '../../contexts/RolePreviewContext';
import SideBarLegacy from './SideBar.legacy';

interface NavItem {
    to: string;
    label: string;
    icon: IconType;
    visible: boolean;
}

interface NavGroup {
    label: string | null; // null = ungrouped (Home)
    items: NavItem[];
}

const SideBarGrouped: React.FC = () => {
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const location = useLocation();
    const user = useSelector((state: RootState) => state.auth.user);
    const { settings } = useSystemSettings();
    const { effectiveRole } = useRolePreview();
    const role = effectiveRole || user?.role;
    const workspaceEnabled = settings.WORKSPACE_CONTAINER_ENABLED === 'true';
    const workspaceViewer = role === 'itadmin' || role === 'superadmin' || role === 'itoperator';

    const groups: NavGroup[] = [
        {
            label: null,
            items: [
                { to: '/', label: 'Home', icon: FiHome, visible: true },
                { to: '/help', label: 'Help', icon: FiHelpCircle, visible: true },
            ],
        },
        {
            label: 'Accounts',
            items: [
                { to: '/user-table', label: 'Server Accounts', icon: FiUsers, visible: role === 'itoperator' || role === 'itadmin' || role === 'superadmin' },
                { to: '/uniqueusers', label: 'Unique Users', icon: FiUserCheck, visible: role === 'itoperator' || role === 'itadmin' || role === 'superadmin' },
                { to: '/gen-users', label: 'User Generation', icon: FiUserPlus, visible: role === 'itoperator' || role === 'itadmin' || role === 'superadmin' },
            ],
        },
        {
            label: 'Licenses',
            items: [
                { to: '/licmgmt', label: 'License Administration', icon: FiKey, visible: role === 'itoperator' || role === 'itadmin' || role === 'superadmin' },
                { to: '/extension-approvals', label: 'Extension Approvals', icon: FiCalendar, visible: role === 'itadmin' || role === 'approver' || role === 'superadmin' },
            ],
        },
        {
            label: 'Operations',
            items: [
                { to: '/server-info', label: 'Server Info', icon: FiServer, visible: role === 'itadmin' || role === 'superadmin' },
                { to: '/pwdreset', label: 'Password Reset', icon: FiLock, visible: (role === 'itadmin' || role === 'superadmin') && settings.PASSWORD_RESET_ENABLED === 'true' },
                { to: '/cleanup-tracking', label: 'Cleanup Tracking', icon: FiTrash2, visible: role === 'itadmin' || role === 'superadmin' },
            ],
        },
        {
            label: 'Browser workspace (beta)',
            items: [
                { to: '/workspace/definitions', label: 'Definitions', icon: FiBox, visible: workspaceEnabled && workspaceViewer },
                { to: '/workspace/nfs', label: 'NFS Locations', icon: FiHardDrive, visible: workspaceEnabled && workspaceViewer },
                { to: '/workspace/groups', label: 'Groups', icon: FiLayers, visible: workspaceEnabled && workspaceViewer },
                { to: '/workspace/sessions', label: 'Sessions', icon: FiMonitor, visible: workspaceEnabled && workspaceViewer },
            ],
        },
        {
            label: 'Insights',
            items: [
                { to: '/reports/login-activity', label: 'Login Activity Report', icon: FiBarChart2, visible: role === 'itoperator' || role === 'itadmin' || role === 'observer' || role === 'superadmin' || role === 'approver' },
                { to: '/audit', label: 'Audit Logs', icon: FiSearch, visible: role === 'auditor' || role === 'superadmin' },
            ],
        },
        {
            label: 'System',
            items: [
                { to: '/user-management', label: 'User Administration', icon: FiShield, visible: role === 'superadmin' },
                { to: '/system-settings', label: 'Configuration', icon: FiSettings, visible: role === 'superadmin' },
            ],
        },
    ];

    return (
        <div>
            <div
                className={`font-plex fixed top-16 left-0 h-[calc(100vh-4rem)] bg-blue-900 border-r border-black/20 text-white transition-all duration-300 z-40 ${
                    isSidebarOpen ? 'w-64' : 'w-16'
                }`}
            >
                <div className="flex items-center justify-between p-4">
                    <h1 className={`${isSidebarOpen ? 'block' : 'hidden'} text-lg font-semibold`}>infra-console</h1>
                    <button onClick={toggleSidebar} className="focus:outline-none text-white/70 hover:text-white">
                        {isSidebarOpen ? <FiX className="text-2xl" /> : <FiMenu className="text-2xl" />}
                    </button>
                </div>

                <nav className="mt-2">
                    {groups.map((group, groupIndex) => {
                        const visibleItems = group.items.filter(item => item.visible);
                        if (visibleItems.length === 0) return null; // role-honest: omit empty groups entirely

                        return (
                            <div key={group.label ?? `group-${groupIndex}`} className="mb-1">
                                {group.label && (
                                    <div className={`${isSidebarOpen ? 'block' : 'hidden'} px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/50`}>
                                        {group.label}
                                    </div>
                                )}
                                <ul>
                                    {visibleItems.map(item => {
                                        const active = location.pathname === item.to;
                                        const Icon = item.icon;
                                        return (
                                            <li key={item.to}>
                                                <Link
                                                    to={item.to}
                                                    title={!isSidebarOpen ? item.label : undefined}
                                                    className={`px-4 py-2 flex items-center border-l-[3px] text-sm ${
                                                        active
                                                            ? 'bg-white/10 border-white text-white font-medium'
                                                            : 'border-transparent text-white/70 hover:bg-white/5 hover:text-white'
                                                    }`}
                                                >
                                                    <Icon className="text-lg flex-shrink-0" />
                                                    <span className={`${isSidebarOpen ? 'ml-3' : 'hidden'} truncate`}>{item.label}</span>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </nav>
            </div>
        </div>
    );
};

const SideBar: React.FC = () => {
    const useNew = useNewGui();
    return useNew ? <SideBarGrouped /> : <SideBarLegacy />;
};

export default SideBar;
